import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { MissionType, MissionTargetType } from '@/types/admin';

const MISSION_TYPES: { value: MissionType; label: string }[] = [
  { value: 'DAILY', label: 'Diária' },
  { value: 'WEEKLY', label: 'Semanal' },
  { value: 'MONTHLY', label: 'Mensal' },
  { value: 'SPECIAL', label: 'Especial' },
];

const TARGET_TYPES: { value: MissionTargetType; label: string }[] = [
  { value: 'lessons_completed', label: 'Lições completadas' },
  { value: 'trails_completed', label: 'Trilhas completadas' },
  { value: 'xp_earned', label: 'XP acumulado' },
  { value: 'games_played', label: 'Jogos jogados' },
  { value: 'streak_days', label: 'Dias de streak' },
  { value: 'quiz_passed', label: 'Quizzes aprovados' },
];

const AdminMissionFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditing = !!id;

  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'DAILY' as MissionType,
    target_type: 'lessons_completed' as MissionTargetType,
    target_count: 3,
    xp_reward: 100,
    icon: 'flag',
    is_active: true,
    auto_assign: true,
    start_date: '',
    end_date: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditing) loadMission();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMission = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const mission = await adminService.getMission(id);
      setForm({
        title: mission.title,
        description: mission.description,
        type: mission.type,
        target_type: mission.target_type,
        target_count: mission.target_count,
        xp_reward: mission.xp_reward,
        icon: mission.icon || 'flag',
        is_active: mission.is_active,
        auto_assign: mission.auto_assign,
        start_date: mission.start_date ? mission.start_date.slice(0, 16) : '',
        end_date: mission.end_date ? mission.end_date.slice(0, 16) : '',
      });
    } catch {
      showToast('Erro ao carregar missão', 'error');
      navigate('/admin/missoes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast('Título é obrigatório', 'warning');
      return;
    }
    if (!form.description.trim()) {
      showToast('Descrição é obrigatória', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };

      if (isEditing && id) {
        await adminService.updateMission(id, payload);
        navigate('/admin/missoes');
      } else {
        const result = await adminService.createMission(payload);
        if (result.auto_assigned_count > 0) {
          showToast(`Missão criada! Atribuída automaticamente a ${result.auto_assigned_count} learner(s).`, 'success');
        }
        navigate('/admin/missoes');
      }
    } catch {
      showToast('Erro ao salvar missão', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-10 text-content-muted">Carregando...</div>;
  }

  return (
    <div className="p-6 lg:p-10 max-w-3xl">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/admin/missoes')} className="text-content-muted hover:text-content-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Editar Missão' : 'Nova Missão'}</h1>
      </div>

      <div className="space-y-6">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-1">Título *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            placeholder="Nome da missão"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1">Descrição *</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none resize-none"
            placeholder="Descrição da missão"
          />
        </div>

        {/* Type + Target Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as MissionType })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            >
              {MISSION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de Meta</label>
            <select
              value={form.target_type}
              onChange={(e) => setForm({ ...form, target_type: e.target.value as MissionTargetType })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            >
              {TARGET_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Target Count + XP Reward + Icon */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Quantidade Alvo</label>
            <input
              type="number"
              value={form.target_count}
              onChange={(e) => setForm({ ...form, target_count: Number(e.target.value) })}
              onFocus={(e) => e.target.select()}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              min={1}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">XP de Recompensa</label>
            <input
              type="number"
              value={form.xp_reward}
              onChange={(e) => setForm({ ...form, xp_reward: Number(e.target.value) })}
              onFocus={(e) => e.target.select()}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
              min={0}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Ícone (Material Symbol)</label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                className="flex-1 px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
                placeholder="Ex: flag, trophy"
              />
              {form.icon && (
                <span className="material-symbols-outlined text-2xl text-accent">{form.icon}</span>
              )}
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Data Início (opcional)</label>
            <input
              type="datetime-local"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Data Fim (opcional)</label>
            <input
              type="datetime-local"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
            />
          </div>
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-5 h-5 rounded border-theme-border text-accent focus:ring-accent"
            />
            <span className="text-sm font-medium">Ativa</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.auto_assign}
              onChange={(e) => setForm({ ...form, auto_assign: e.target.checked })}
              className="w-5 h-5 rounded border-theme-border text-accent focus:ring-accent"
            />
            <div>
              <span className="text-sm font-medium">Auto-assign</span>
              <p className="text-xs text-content-muted">Atribuir automaticamente a todos os learners ativos</p>
            </div>
          </label>
        </div>

        {/* Save */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Missão'}
          </button>
          <button
            onClick={() => navigate('/admin/missoes')}
            className="px-6 py-2.5 rounded-xl font-medium border border-theme-border text-content-muted hover:text-content-primary transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMissionFormPage;
